import { DefaultRate } from '../models/DefaultRate'

/**
 * Given a DefaultRate, returns its duration in hours.
 * I tried implementing this as a Vuex ORM accessor, but the accessor didn't
 * get called when I referenced defaultRate.durationInHours - I just got
 * undefined.
 * https://vuex-orm.org/guide/model/accessors-and-mutators.html#defining-accessors
 */
export default function getDefaultRateDurationInHours(defaultRate) {
  if (!defaultRate.perUnit || !defaultRate.perQuantity) {
    return 0
  }
  switch (defaultRate.perUnit) {
    case 'hour':
      return defaultRate.perQuantity
      break
    case 'day':
      return defaultRate.perQuantity * 24
  }
}

/**
 * Builds formState suitable for passing as the v-model for the RateSet
 * component.
 * 
 * defaultRates: An array of DefaultRate models, with their ChannelDefaultRate
 * relations already loaded.
 * 
 * channelFacilities: An array of ChannelFacility models which belong to the
 * location that these rates are for.
 */
export function buildRateSetFormState(defaultRates, channelFacilities) {

  const output = []

  defaultRates
    .sort((rate1, rate2) => {
      const rate1Duration = getDefaultRateDurationInHours(rate1)
      const rate2Duration = getDefaultRateDurationInHours(rate2)
      if (rate1Duration > rate2Duration) {
        return 1
      } else if (rate1Duration < rate2Duration) {
        return -1
      }
      return 0
    })
    .forEach((defaultRate) => {
  
      const formCdrs = channelFacilities.map((channelFacility) => {

        const channelDefaultRate = defaultRate.channelDefaultRates.find((channelDefaultRate) => {
          return channelDefaultRate.channelFacilitiesID === channelFacility.ID
        })

        const originalChannelCost = channelDefaultRate ? channelDefaultRate.channelCost : null
        const formChannelCost = originalChannelCost && originalChannelCost !== defaultRate.defaultCost ? originalChannelCost : null

        return {
          ID: channelDefaultRate ? channelDefaultRate.ID : null,
          channelCost: formChannelCost,
          channelFacility: channelFacility, // included so that RateFormChannelRate can display an appropriate label, and so that save logic knows what channelFacilitiesID to send to the API
        }

      })

      output.push({
        ID: defaultRate.ID,
        defaultCost: defaultRate.defaultCost,
        perQuantity: defaultRate.perQuantity,
        perUnit: defaultRate.perUnit,
        channelDefaultRates: formCdrs,
      })

    })

  return output

}

/**
 * Given an array of form items, issues API calls to create, update, and/or
 * delete them (and their associated ChannelDefaultRates).
 * 
 * Returns a promise.
 * 
 * formDefaultRates: An array of form items to be saved, in the format returned
 * by buildRateSetFormState().
 */
export function saveRateSetFormState(
  locationID,
  locationProductID,
  formDefaultRates,
  originalDefaultRateIDs,
  onlyChannelFacilityIds = null,
  bypassWorkers = false,
) {

  // Remember the ID of each existing DefaultRate that we update. We'll use
  // this below to determine which DRs have been removed from the form (and
  // therefore need to be deleted).
  const updatedDefaultRateIDs = []

  const createPayload = formDefaultRates
    .filter((formDefaultRate) => !formDefaultRate.ID)
    .map((formDefaultRate) => {

      const formCdrs = filterFormCdrsByChannelFacilityId(formDefaultRate.channelDefaultRates, onlyChannelFacilityIds)

      const channelDefaultRates = formCdrs
        .filter((formCdr) => {
          // Don't send CDRs whose cost is either (1) empty, or (2) the same as 
          // the default cost. The BE will implicitly create CDRs for these.
          const fieldIsEmpty = formCdr.channelCost === '' || formCdr.channelCost === null
          const channelCostDiffersFromDefaultCost = formCdr.channelCost !== formDefaultRate.defaultCost
          return !fieldIsEmpty && channelCostDiffersFromDefaultCost
        })
        .map((formCdr) => {
          return {
            channelCost: formCdr.channelCost,
            channelFacilitiesID: formCdr.channelFacility.ID,
          }
        })

      return {
        locationProductID: locationProductID,
        defaultCost: formDefaultRate.defaultCost,
        perQuantity: formDefaultRate.perQuantity,
        perUnit: formDefaultRate.perUnit,
        channelDefaultRates: channelDefaultRates,
      }

    })

  const updatePayload = formDefaultRates
    .filter((formDefaultRate) => !!formDefaultRate.ID)
    .map((formDefaultRate) => {

      const formCdrs = filterFormCdrsByChannelFacilityId(formDefaultRate.channelDefaultRates, onlyChannelFacilityIds)

      const channelDefaultRates = formCdrs
        .map((formCdr) => {
          const fieldIsEmpty = formCdr.channelCost === '' || formCdr.channelCost === null
          return {
            ...formCdr.ID && {channelDefaultRateID: formCdr.ID}, // https://medium.com/james-hill/object-literal-conditional-properties-4d9312b7741e
            channelCost: fieldIsEmpty ? formDefaultRate.defaultCost : formCdr.channelCost,
            channelFacilitiesID: formCdr.channelFacility.ID,
          }
        })

      updatedDefaultRateIDs.push(formDefaultRate.ID)

      return {
        defaultRateID: formDefaultRate.ID,
        defaultCost: formDefaultRate.defaultCost,
        channelDefaultRates: channelDefaultRates,
      }

    })

  /**
   * Build the API requests
   */

  const promises = []

  if (createPayload.length) {
    promises.push(DefaultRate.bulkCreate(locationID, createPayload, bypassWorkers))
  }

  if (updatePayload.length) {
    promises.push(DefaultRate.bulkUpdate(updatePayload, bypassWorkers))
  }

  // Delete any DefaultRates that have been removed
  originalDefaultRateIDs.forEach((originalDefaultRateID) => {
    if (!updatedDefaultRateIDs.includes(originalDefaultRateID)) {
      promises.push(
        DefaultRate.destroy(originalDefaultRateID, bypassWorkers)
      )
    }
  })

  return Promise.all(promises)

}

function filterFormCdrsByChannelFacilityId(formCdrs, onlyChannelFacilityIds) {
  return formCdrs.filter((formCdr) => {
    if (onlyChannelFacilityIds === null) {
      return true
    }
    return onlyChannelFacilityIds.includes(formCdr.channelFacility.ID)
  })
}
